import React, { useState, useEffect } from 'react';
import { subscribeToModalState } from '../../utils/modalState';
import { MessageCircle } from 'lucide-react';
import { Sidebar } from './Sidebar';
import MobileNav from './MobileNav';
import { CoachSteve } from '../coach-steve/CoachSteve';
import { Footer } from './Footer';

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [coachStevePanelOpen, setCoachStevePanelOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    return subscribeToModalState(setIsModalOpen);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      {/*
        Dark scrims behind Android's transparent system bars (edge-to-edge enforced on SDK 35+).
        Heights come from native -> CSS variables (already converted to CSS px in MainActivity).
      */}
      <div
        className="md:hidden pointer-events-none fixed left-0 right-0 top-0 z-[60] bg-[#111827]"
        style={{ height: 'var(--safe-area-inset-top, 0px)' }}
      />
      <div
        className="md:hidden pointer-events-none fixed left-0 right-0 bottom-0 z-[40] bg-[#111827]"
        style={{ height: 'var(--safe-area-inset-bottom, 0px)' }}
      />

      <div className="flex flex-1 min-h-0">
        {/* Desktop Sidebar - hidden on mobile */}
        <div className="hidden md:block">
          <Sidebar />
        </div>

        {/*
          Main Content
          - Mobile: top padding clears the Android status bar (1rem + safe-area-inset-top),
            bottom padding clears the bottom mobile nav + system nav (6rem + safe-area-inset-bottom).
          - Desktop: simple uniform 2rem padding.
        */}
        <main className="flex-1 overflow-auto px-4 md:px-8 pt-[calc(1rem+var(--safe-area-inset-top,0px))] md:pt-8 pb-[calc(6rem+var(--safe-area-inset-bottom,0px))] md:pb-8">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation - sits directly above the dark bottom scrim */}
      {!isModalOpen && (
        <div
          className="md:hidden fixed left-0 right-0 z-50"
          style={{ bottom: 'var(--safe-area-inset-bottom, 0px)' }}
        >
          <MobileNav />
        </div>
      )}

      {/* AI Coach tab - opens right-side panel (keeps bottom-right clear for other FAB menus) */}
      <button
        type="button"
        onClick={() => setCoachStevePanelOpen(true)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-2 pl-3 pr-4 py-3 rounded-l-full bg-primary text-white shadow-lg hover:bg-primary/90 active:bg-primary/95 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${isModalOpen ? 'hidden' : ''}`}
        aria-label="Open AI Coach"
      >
        <MessageCircle className="h-5 w-5" />
        
      </button>

      {/* Coach Steve off-canvas panel - always mounted to preserve chat history */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 ease-out ${
          coachStevePanelOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!coachStevePanelOpen}
      >
        {/* Backdrop */}
        <button
          type="button"
          onClick={() => setCoachStevePanelOpen(false)}
          className="absolute inset-0 bg-black/40 transition-opacity"
          aria-label="Close Coach Steve"
        />
        {/* Panel — bottom is pushed up by the safe-area inset so it clears the Android system nav */}
        <div
          className={`absolute top-0 right-0 w-full max-w-md bg-cream shadow-xl flex flex-col transition-transform duration-300 ease-out ${
            coachStevePanelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{
            height: 'calc(100% - var(--safe-area-inset-bottom, 0px))',
          }}
        >
          <CoachSteve isPanel onClose={() => setCoachStevePanelOpen(false)} />
        </div>
      </div>

      {/* Footer - desktop only (mobile shows on Dashboard only) */}
      <Footer className="hidden md:block flex-shrink-0" />
    </div>
  );
};

export default MainLayout; 